<script setup lang="ts">
// v3.17.4 任务详情 v2 — 全屏视图 (左侧菜单独立常驻, 详情占满内容区)。
// 数据: 状态 1s / live 1s (块图+线程+服务器) / 文件 3s / BT peers 3s。
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'
import {
  getTask,
  getTaskFiles,
  getTaskUris,
  getTaskPeers,
  getTaskServers,
  getTaskLive,
  getTaskLog,
  pauseTask,
  resumeTask,
  retryTask,
  removeTask,
  type TaskInfo,
  type TaskLive,
  type TaskLogLine,
  type LiveServer,
  type TaskPeer,
  type LiveWorker,
} from '@/api/tasks'
import { useTaskActions } from '@/composables/useTaskActions'
import { formatBytes, formatSpeed } from '@/composables/useFormat'
import TaskSpeedChart from '@/components/tasks/TaskSpeedChart.vue'
import BlockMap from '@/components/tasks/BlockMap.vue'
import FlagBadge from '@/components/tasks/FlagBadge.vue'
import TaskRemoveConfirm from '@/components/tasks/TaskRemoveConfirm.vue'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const actions = useTaskActions()

const gid = computed(() => String(route.params.gid ?? ''))

const statusQuery = useQuery({
  queryKey: ['task', gid],
  queryFn: () => getTask(gid.value),
  refetchInterval: 1000,
  staleTime: 0,
  enabled: () => !!gid.value,
})

const filesQuery = useQuery({
  queryKey: ['task-files', gid],
  queryFn: () => getTaskFiles(gid.value),
  refetchInterval: 3000,
  enabled: () => !!gid.value,
})

const urisQuery = useQuery({
  queryKey: ['task-uris', gid],
  queryFn: () => getTaskUris(gid.value),
  enabled: () => !!gid.value,
})

const liveQuery = useQuery({
  queryKey: ['task-live', gid],
  queryFn: () => getTaskLive(gid.value),
  refetchInterval: 1000,
  enabled: () => !!gid.value,
})

// v3.18.0 任务日志 (2s 轮询尾部)
const logQuery = useQuery({
  queryKey: ['task-log', gid],
  queryFn: () => getTaskLog(gid.value, 200),
  refetchInterval: 2000,
  enabled: () => !!gid.value,
})
const logLines = computed<TaskLogLine[]>(() => logQuery.data.value ?? [])

const task = computed<TaskInfo | undefined>(() => statusQuery.data.value)
const live = ref<TaskLive | null>(null)

// live 轮询: 204 (任务已结束) 时保留最后一次数据, 停止条件交给 status
watch(liveQuery.data, (v) => {
  if (v) live.value = v
})

const isTorrent = computed(() => !!task.value?.infoHash)
const isRunning = computed(() =>
  ['active', 'downloading', 'waiting'].includes(String(task.value?.status ?? '')),
)
const mode = computed(() => live.value?.mode ?? (isTorrent.value ? 'bt' : 'none'))

// peers (BT)
const peersQuery = useQuery({
  queryKey: ['task-peers', gid],
  queryFn: () => getTaskPeers(gid.value),
  refetchInterval: 3000,
  enabled: () => !!gid.value && isTorrent.value,
})
const peers = computed<TaskPeer[]>(() => peersQuery.data.value ?? [])

const serversQuery = useQuery({
  queryKey: ['task-servers', gid],
  queryFn: () => getTaskServers(gid.value),
  enabled: () => !!gid.value && isTorrent.value,
})
const trackers = computed(() => serversQuery.data.value ?? [])

const files = computed(() => filesQuery.data.value ?? [])
const uris = computed(() => {
  const u = urisQuery.data.value ?? []
  return u.length > 0 ? u : task.value?.files?.[0]?.uris ?? []
})

const progressPct = computed(() => {
  const total = Number(task.value?.totalLength ?? 0)
  const done = Number(task.value?.completedLength ?? 0)
  if (!total) return 0
  return Math.min(100, (done / total) * 100)
})

const totalBlocks = computed(() => {
  if (!live.value?.blocksOk) return 0
  const b64 = live.value.blocksCompleted ?? ''
  if (!b64) return 0
  return b64.length === 0 ? 0 : b64.length * 8 // 近似: base64 → bits
})

const workers = computed<Array<LiveWorker & { _pct: number }>>(() => {
  const ws = live.value?.workers ?? []
  const max = Math.max(1, ...ws.map((w) => w.speedBps))
  return ws.map((w) => ({ ...w, _pct: Math.round((w.speedBps / max) * 100) }))
})

const servers = computed<LiveServer[]>(() => live.value?.servers ?? [])
const totalServerBytes = computed(() => servers.value.reduce((a, s) => a + s.bytes, 0))

// v3.19.0: 连接故事 — 协议徽章 + 链路字段 (tellStatus 新字段)。
const PROTOCOL_LABELS: Record<string, string> = {
  http: 'HTTP(S)直链',
  ftp: 'FTP',
  ftps: 'FTPS',
  sftp: 'SFTP',
  hls: 'HLS流',
  bt: 'BT种子',
  magnet: '磁力',
  manual: '手动',
}
const protocolLabel = computed(() => {
  const p = String(task.value?.protocol ?? '')
  if (p) return PROTOCOL_LABELS[p] ?? p
  return isTorrent.value ? 'BT种子' : 'HTTP(S)直链'
})
const resolvedUrl = computed(() => task.value?.resolvedUrl ?? '')
const redirectCount = computed(() => Number(task.value?.redirectCount ?? 0))
const proxyInUse = computed(() => task.value?.proxy ?? '')
const hasLinkStory = computed(() => !!(resolvedUrl.value || proxyInUse.value))

const fmtSpeed = (bps: number) => (bps > 0 ? formatSpeed(bps) : '—')

function goBack(): void {
  if (window.history.length > 1) router.back()
  else void router.push('/tasks')
}

const busy = ref(false)

function levelClass(level: string): string {
  return `log-${level.toUpperCase()}`
}

async function doPause(): Promise<void> {
  if (!task.value) return
  busy.value = true
  try {
    await actions.pause(task.value)
  } finally {
    busy.value = false
  }
}
async function doResume(): Promise<void> {
  if (!task.value) return
  busy.value = true
  try {
    await actions.resume(task.value)
  } finally {
    busy.value = false
  }
}
async function doRetry(): Promise<void> {
  if (!task.value) return
  busy.value = true
  try {
    await actions.retry(task.value)
  } finally {
    busy.value = false
  }
}
async function doRemove(): Promise<void> {
  if (!task.value) return
  busy.value = true
  try {
    // v3.19.31: remove 现在先弹确认 (全节点同步删), 确认成功才回列表页。
    const deleted = await actions.remove(task.value)
    if (deleted) goBack()
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <div class="flex flex-col h-full min-h-0">
    <!-- 头部 -->
    <div class="flex items-center gap-3 px-6 py-3.5 bg-white border-b border-slate-200">
      <h1 class="text-lg font-bold text-slate-800 flex-1 truncate">
        {{ task?.files?.[0]?.path?.split(/[\\/]/).pop() ?? task?.dir ?? gid }}
      </h1>
      <span
        v-if="task"
        class="text-[11px] font-semibold px-3 py-1 rounded-full"
        :class="{
          'bg-green-100 text-green-700': task.status === 'downloading' || task.status === 'active',
          'bg-slate-100 text-slate-600': task.status === 'paused' || task.status === 'waiting',
          'bg-red-100 text-red-700': task.status === 'error',
          'bg-indigo-100 text-indigo-700': task.status === 'complete',
        }"
      >{{ task.status }}</span>
      <span class="modetab on">{{ protocolLabel }}</span>
      <span class="text-xs text-slate-400 font-mono">gid {{ gid }}</span>
      <button class="w-8 h-8 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700" @click="goBack">✕</button>
    </div>

    <!-- 内容 -->
    <div class="flex-1 overflow-y-auto p-6 space-y-4">
      <!-- 进度概览 -->
      <div>
        <div class="h-3 rounded-md bg-slate-200 overflow-hidden">
          <div class="h-full rounded-md bg-gradient-to-r from-indigo-400 to-indigo-500" :style="{ width: progressPct + '%' }"></div>
        </div>
        <div class="grid grid-cols-5 gap-2.5 mt-3">
          <div class="st"><div class="k">已下载</div><div class="v">{{ formatBytes(Number(task?.completedLength ?? 0)) }} <small>/ {{ formatBytes(Number(task?.totalLength ?? 0)) }}</small></div></div>
          <div class="st"><div class="k">速度</div><div class="v">{{ formatSpeed(Number(task?.downloadSpeed ?? 0)) }}</div></div>
          <div class="st"><div class="k">连接</div><div class="v">{{ live?.threadsActive ?? 0 }} <small>活跃</small></div></div>
          <div class="st"><div class="k">块</div><div class="v">{{ totalBlocks ? totalBlocks : '—' }}</div></div>
          <div class="st"><div class="k">状态</div><div class="v text-sm">{{ task?.status ?? '—' }}</div></div>
        </div>
      </div>

      <!-- 块图 + 线程 -->
      <div class="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        <div v-if="live?.blocksOk" class="card">
          <h3>块图</h3>
          <BlockMap
            :completed="live.blocksCompleted ?? ''"
            :in-flight="live.blocksInFlight ?? ''"
            :block-length="live.blockLength ?? 0"
          />
        </div>
        <div class="card" :class="{ 'xl:col-span-2': !live?.blocksOk }">
          <h3>线程明细 <span class="count">{{ workers.filter(w => w.active).length }} 活跃</span></h3>
          <div v-if="workers.length === 0" class="text-xs text-slate-400 py-2">暂无线程数据 (任务未在下载中)</div>
          <div v-for="w in workers" :key="w.id" class="wrow">
            <span class="id">#{{ w.id }}</span>
            <span class="blk">{{ w.blocks || '—' }}</span>
            <span class="spd">{{ fmtSpeed(w.speedBps) }}</span>
            <div class="wbar"><i :style="{ width: w._pct + '%' }"></i></div>
          </div>
          <div v-if="workers.length" class="wnote">速度条以当前最快线程为基准 · 每秒刷新</div>
        </div>
      </div>

      <!-- 服务器 (HTTP) -->
      <div v-if="mode !== 'bt' && servers.length" class="card">
        <h3>服务器 <span class="count">{{ servers.length }} 台</span></h3>
        <div v-for="s in servers" :key="s.ip + s.port" class="srv">
          <div class="min-w-0">
            <div class="ip flex items-center"><FlagBadge :ip="s.ip" />{{ s.ip }}:{{ s.port }}</div>
            <div class="host">共 {{ s.conns }} 连接 · 累计 {{ formatBytes(s.bytes) }}</div>
          </div>
          <div class="spd">{{ fmtSpeed(s.speedBps) }}</div>
          <div class="share"><i :style="{ width: totalServerBytes ? Math.round((s.bytes / totalServerBytes) * 100) + '%' : '0%' }"></i></div>
        </div>
        <div v-if="servers.length === 0" class="text-xs text-slate-400 py-1">尚无连接</div>
      </div>

      <!-- 连接链路 (v3.19.0): 来源 → 重定向 → 最终地址 → 代理 -->
      <div v-if="hasLinkStory" class="card">
        <h3>连接链路</h3>
        <div class="text-xs space-y-1.5">
          <div class="flex gap-2 items-start">
            <span class="w-16 shrink-0 text-slate-400">来源</span>
            <span class="font-mono break-all text-slate-700">{{ task?.url ?? task?.files?.[0]?.uris?.[0]?.uri ?? '—' }}</span>
          </div>
          <div v-if="redirectCount > 0 && resolvedUrl" class="flex gap-2 items-start">
            <span class="w-16 shrink-0 text-slate-400">重定向</span>
            <span class="break-all">{{ redirectCount }} 次 → <span class="font-mono text-slate-700">{{ resolvedUrl }}</span></span>
          </div>
          <div v-else-if="resolvedUrl" class="flex gap-2 items-start">
            <span class="w-16 shrink-0 text-slate-400">最终地址</span>
            <span class="font-mono break-all text-slate-700">{{ resolvedUrl }}</span>
          </div>
          <div v-if="proxyInUse" class="flex gap-2 items-start">
            <span class="w-16 shrink-0 text-slate-400">代理</span>
            <span class="font-mono text-slate-700">{{ proxyInUse }}</span>
          </div>
        </div>
      </div>

      <!-- 对端 (BT) -->
      <div v-if="mode === 'bt'" class="card">
        <h3>连接的对端 <span class="count">{{ peers.length }} 个</span></h3>
        <div v-if="peers.length === 0" class="text-xs text-slate-400 py-1">未连接任何 peer</div>
        <div v-for="(p, i) in peers" :key="p.ip + ':' + p.port + i" class="peer">
          <div class="min-w-0">
            <div class="ip flex items-center"><FlagBadge :ip="p.ip" />{{ p.ip }}:{{ p.port }}</div>
            <div class="cli">{{ p.clientName ?? p.peerId ?? '' }}</div>
          </div>
          <div class="dl">↓ {{ formatSpeed(Number(p.downloadSpeed ?? 0)) }}</div>
          <div class="ul">↑ {{ formatSpeed(Number(p.uploadSpeed ?? 0)) }}</div>
          <div class="pct">{{ p.percentCompleted ?? '—' }}%</div>
        </div>
      </div>

      <!-- Tracker (BT) -->
      <div v-if="mode === 'bt' && trackers.length" class="card">
        <h3>Tracker <span class="count">{{ trackers.length }} 个</span></h3>
        <div v-for="(tr, i) in trackers" :key="i" class="srv">
          <div class="min-w-0">
            <div class="ip flex items-center"><FlagBadge :ip="tr.uri ?? ''" />{{ tr.uri }}</div>
            <div class="host">{{ tr.currentUri ?? '' }}</div>
          </div>
          <div class="spd">{{ tr.downloadSpeed }} peer</div>
          <div class="share"><i style="width:0%"></i></div>
        </div>
      </div>

      <!-- 速度曲线 + 来源 -->
      <div class="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        <div class="card">
          <h3>速度曲线 · 60s</h3>
          <TaskSpeedChart :gid="gid" :height="130" />
        </div>
        <div class="card">
          <h3>来源 / 文件 <span class="count">{{ files.length || uris.length }} 项</span></h3>
          <div v-if="files.length" v-for="f in files" :key="f.index" class="srv">
            <div class="min-w-0">
              <div class="ip" style="font-family:inherit">{{ f.path.split(/[\\/]/).pop() }}</div>
              <div class="host">{{ f.path }}</div>
            </div>
            <div class="spd">{{ formatBytes(Number(f.completedLength ?? 0)) }}</div>
            <div class="share"><i :style="{ width: Number(f.length) ? Math.round((Number(f.completedLength) / Number(f.length)) * 100) + '%' : '0%' }"></i></div>
          </div>
          <div v-if="!files.length" v-for="(u, i) in uris" :key="i" class="text-xs text-slate-500 font-mono truncate py-1">{{ u.uri }}</div>
          <div v-if="!files.length && !uris.length" class="text-xs text-slate-400 py-1">—</div>
        </div>
      </div>

      <!-- 错误区 -->
      <div v-if="task?.errorMessage" class="card border-red-200 bg-red-50">
        <h3 class="text-red-600">错误</h3>
        <div class="text-sm text-red-700 font-mono break-all">{{ task.errorMessage }}</div>
      </div>

      <!-- 任务日志 (v3.18.0) -->
      <div class="card">
        <h3>任务日志 <span class="count">{{ logLines.length }} 行</span></h3>
        <div v-if="logLines.length === 0" class="text-xs text-slate-400 py-1">暂无日志</div>
        <div v-else class="logbox font-mono text-[11px] leading-5">
          <div v-for="(l, i) in logLines" :key="i" class="whitespace-pre-wrap break-all">
            <span class="text-slate-400">{{ l.ts.slice(11, 19) }}</span>
            <span :class="levelClass(l.level)"> [{{ l.level.toUpperCase() }}]</span>
            <span class="text-slate-700"> {{ l.msg }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部操作 -->
    <div class="flex gap-2.5 px-6 py-3 bg-white border-t border-slate-200">
      <button v-if="task?.status === 'paused'" class="btn" :disabled="busy" @click="doResume">▶ 继续</button>
      <button v-else class="btn" :disabled="busy" @click="doPause">⏸ 暂停</button>
      <button class="btn" :disabled="busy" @click="doRetry">↻ 重试</button>
      <button class="btn warn" :disabled="busy" @click="doRemove">🗑 删除任务</button>
    </div>

    <!-- v3.19.31: 删除统一确认 (全节点同步删 + 可选删文件) -->
    <TaskRemoveConfirm />
  </div>
</template>

<style scoped>
.modetab { font-size: 11px; padding: 3px 10px; border-radius: 7px; border: 1px solid #e2e8f0;
  background: #f8fafc; color: #64748b; cursor: pointer; user-select: none; }
.modetab.on { background: #6366f1; color: #fff; border-color: #6366f1; }
.card { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 14px 16px; }
.card > h3 { font-size: 11.5px; font-weight: 700; color: #94a3b8; letter-spacing: 0.06em;
  text-transform: uppercase; margin-bottom: 9px; display: flex; align-items: center; gap: 8px; }
.count { font-size: 10.5px; font-weight: 600; background: #eef2ff; color: #6366f1;
  padding: 1px 7px; border-radius: 999px; }
.st { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 11px; padding: 9px 12px; }
.st .k { font-size: 10.5px; color: #94a3b8; margin-bottom: 3px; }
.st .v { font-size: 15px; font-weight: 700; color: #1e293b; }
.st .v small { font-size: 10.5px; color: #64748b; font-weight: 400; }
.wrow { display: grid; grid-template-columns: 34px 100px 80px 1fr; gap: 12px; align-items: center;
  padding: 8px 12px; border-radius: 10px; margin-bottom: 6px; background: #f8fafc;
  border: 1px solid #e2e8f0; }
.wrow .id { font-size: 12.5px; font-weight: 700; color: #6366f1; font-family: Consolas, monospace; }
.wrow .blk { font-size: 12px; color: #64748b; font-family: Consolas, monospace; }
.wrow .spd { font-size: 12.5px; font-weight: 700; color: #1e293b; text-align: right; }
.wbar { height: 5px; border-radius: 3px; background: #e8ecf4; overflow: hidden; }
.wbar > i { display: block; height: 100%; border-radius: 3px;
  background: linear-gradient(90deg, #fbbf24, #f59e0b); }
.wnote { font-size: 11px; color: #94a3b8; margin-top: 4px; }
.srv { display: grid; grid-template-columns: 1fr 100px 70px; gap: 10px; align-items: center;
  padding: 8px 12px; border-radius: 10px; margin-bottom: 6px; background: #f8fafc;
  border: 1px solid #e2e8f0; }
.srv .ip { font-size: 12.5px; font-weight: 600; color: #1e293b; font-family: Consolas, monospace; }
.srv .host { font-size: 10.5px; color: #94a3b8; overflow: hidden; text-overflow: ellipsis;
  white-space: nowrap; margin-top: 1px; }
.srv .spd { font-size: 12.5px; font-weight: 700; color: #1e293b; text-align: right; }
.srv .share { height: 4px; border-radius: 2px; background: #e8ecf4; overflow: hidden; }
.srv .share > i { display: block; height: 100%; background: linear-gradient(90deg, #34d399, #10b981); }
.peer { display: grid; grid-template-columns: 1.4fr 74px 74px 52px; gap: 10px; align-items: center;
  padding: 7px 12px; border-radius: 10px; margin-bottom: 5px; background: #f8fafc;
  border: 1px solid #e2e8f0; }
.peer .ip { font-size: 12px; font-weight: 600; color: #1e293b; font-family: Consolas, monospace;
  display: flex; align-items: center; }
.peer .cli { font-size: 10px; color: #94a3b8; margin-top: 1px; }
.peer .dl { font-size: 12px; font-weight: 700; color: #166534; text-align: right; }
.peer .ul { font-size: 11.5px; color: #64748b; text-align: right; }
.peer .pct { font-size: 11.5px; color: #64748b; text-align: right; font-family: Consolas, monospace; }
.btn { padding: 9px 26px; border-radius: 9px; border: 1px solid #e2e8f0; background: #fff;
  font-size: 13px; font-weight: 600; color: #64748b; cursor: pointer; }
.btn:hover { background: #f8fafc; color: #1e293b; }
.btn.warn { color: #ef4444; border-color: #fecaca; }
.btn.warn:hover { background: #fef2f2; }
.btn:disabled { opacity: 0.5; cursor: default; }
.logbox { background: #0f172a; border-radius: 10px; padding: 10px 12px; max-height: 260px;
  overflow-y: auto; }
.logbox .text-slate-400, .logbox .text-slate-700 { color: inherit; }
.log-INFO { color: #7dd3fc; }
.log-WARN { color: #fbbf24; }
.log-ERROR { color: #f87171; font-weight: 700; }
.log-DEBUG { color: #94a3b8; }
</style>
